'use client'

import { useEffect, useRef, useState } from 'react'

import { getCookie, getDomain } from '@/utils/utils'
import axios from '@/utils/assessmentAxiosInterceptor'

import Icon from '@/components/Icon/Icon'
import Button from '@/components/Button/Button'
import Input from '@/components/Input/Input'

import styles from './Auth.module.scss'
import classNames from 'classnames/bind'
const classes = classNames.bind(styles)

export default ({ setEmail, setPassword, setSelectedType, setError, emailParam, firstNameParam, lastNameParam }) => {
    const emailInput = useRef(null)
    const passwordInput = useRef(null)
    const firstNameInput = useRef(null)
    const lastNameInput = useRef(null)

    const [revealPassword, setRevealPassword] = useState(false)
    const [authMethod, setAuthMethod] = useState(false)

    useEffect(() => {
        if (authMethod) {
            const checkLoggedIn = async () => {
                if (!!getCookie('accessToken')) {
                    try {
                        const { data } = await axios.get('/assessment/api/auth/getId')
                        if (data.success) {
                            window.location = data?.redirect ? data.redirect : '/'
                            return
                        }
                    } catch (e) {}
                }

                setTimeout(() => {
                    if (authMethod) checkLoggedIn()
                }, 1000)
            }

            checkLoggedIn()
        }
    }, [authMethod])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
    }, [])

    const submitRegister = async () => {
        setError(null)
        grecaptcha.ready(() => {
            grecaptcha.execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, { action: 'submit' }).then(async (token) => {
                try {
                    const source = emailParam && firstNameParam && lastNameParam ? 'xero' : null
                    const { data } = await axios.post('/assessment/api/auth/register', {
                        email: emailInput.current.value,
                        password: passwordInput.current.value,
                        first_name: firstNameInput.current.value,
                        last_name: lastNameInput.current.value,
                        captchaToken: token,
                        source,
                    })

                    setEmail(emailInput.current.value)
                    setPassword(passwordInput.current.value)
                } catch (e) {
                    if (e?.response?.data?.redirectToSignIn) {
                        setError(e.response.data.message)

                        // Attempt to login automatically.
                        try {
                            const { data } = await axios.post('/assessment/api/auth/login', {
                                email: emailInput.current.value,
                                password: passwordInput.current.value,
                                captchaToken: token,
                            })

                            window.location = '/'
                            return
                        } catch (e) {}
                    }
                    if (e?.response?.data) {
                        setError(e.response.data.message)
                    }
                }
            })
        })
    }

    const handlePasswordEyeClick = () => {
        setRevealPassword(!revealPassword)
        setTimeout(() => {
            passwordInput.current.focus()
        }, 50)
    }

    return (
        <>
            <form
                className={classes('form')}
                onSubmit={(e) => {
                    e.preventDefault()
                    localStorage.removeItem('googleAuth')
                    submitRegister()
                    setAuthMethod(false)
                }}
            >
                <h1 className={classes('title')}>Let's create an account</h1>
                <p className={classes('subtext')}>
                    Your account will allow you to keep all your tax information in one place, easily submit repeat
                    returns and get personalised advice!
                </p>
                <div className={classes('input-container', 'split')}>
                    <Input
                        placeholder="First name"
                        defaultValue={firstNameParam || ''}
                        required={true}
                        ref={firstNameInput}
                    />
                    <Input
                        placeholder="Last name"
                        defaultValue={lastNameParam || ''}
                        required={true}
                        ref={lastNameInput}
                    />
                </div>
                <div className={classes('input-container')}>
                    <Input
                        type="email"
                        placeholder="Your email"
                        required={true}
                        ref={emailInput}
                        className={classes('icon-input')}
                        autoComplete="username"
                        defaultValue={emailParam || ''}
                        disabled={!!emailParam}
                    />
                    <div className={classes('error-icon')}>
                        <Icon name="alert-circle" width={20} height={20} />
                    </div>
                </div>
                <div className={classes('input-container')}>
                    <Input
                        type={revealPassword ? 'text' : 'password'}
                        placeholder="Password"
                        title={
                            'Password must have minimum length of 8 characters  and contain at least 1 lowercase character, 1 uppercase letter, 1 number and 1 special character (!@#$%^&*).'
                        }
                        ref={passwordInput}
                        required={true}
                        className={classes('icon-input')}
                        autoComplete="new-password"
                    />
                    <div
                        className={classes('password-reveal', {
                            'password-reveal--active': revealPassword,
                        })}
                        onMouseDown={handlePasswordEyeClick}
                    >
                        {revealPassword && <Icon name="eye" width={20} height={20} />}
                        {!revealPassword && <Icon name="eye-off" width={20} height={20} />}
                    </div>
                    <div className={classes('password-banner')}>
                        Password must have minimum length of 8 characters and contain at least 1 lowercase character, 1
                        uppercase letter, 1 number and 1 special character:
                        <span>{`(^ $ * . [ ] { } ( ) ? - " ! @ # % & / \\ , > < ' : ; | _ ~ \` + =)`}</span>
                    </div>
                </div>

                <Button className={classes('login-button')} text="Create account" />
            </form>
            <div className={classes('or-separator')}>
                <div className={classes('line')}></div>
                <span>or</span>
                <div className={classes('line')}></div>
            </div>
            <div className={classes('buttons')}>
                <button
                    className={classes('auth-button')}
                    onClick={() => {
                        localStorage.setItem('googleAuth', true)
                        window.open(
                            /* We have to replace the dev to qa cause dev and qa uses one cognito and the url belongs to cognito not to our server */
                            `${getDomain('auth').replace(
                                'dev',
                                'qa'
                            )}/oauth2/authorize?identity_provider=Google&redirect_uri=${getDomain('api').replace(
                                'dev',
                                'qa'
                            )}/assessment/api/auth/authCode&response_type=CODE&client_id=${
                                process.env.NEXT_PUBLIC_ENVIRONMENT == 'prod'
                                    ? '5k3kkl8a93gh62lob5l4j6k50v'
                                    : '1qvgujv2lmsinmkb28d4ctqnt7'
                            }&scope=email openid phone profile`,
                            null,
                            { popup: true }
                        )
                        setAuthMethod(true)
                    }}
                >
                    <Icon name="google" />
                    <span>Sign in with Google</span>
                </button>
            </div>
            <span className={classes('login-link')}>
                Already have an account?{' '}
                <a href="#" onClick={() => setSelectedType('login')}>
                    Log in
                </a>
            </span>
        </>
    )
}
